import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import { CardContent, CardHeader } from '@/components/card/card';
import { Modal } from '@/components';
import { KeenIcon } from '@/components';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useGetHarborAccessInformationQuery,
  useUpdateHarborAccessInformationMutation,
  useTranslateHarborAccessInformationMutation,
} from '@/services/HarborManagementService';
import HarborAccessInfoPreviewModal from './HarborAccessInfoPreviewModal';

const inputClassName =
  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const selectClassName =
  'w-full bg-light-active h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const textareaClassName =
  'w-full textarea px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const labelClassName = 'block text-sm font-medium text-gray-700 mb-2';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HarborAccessInformation = ({ harbor }: { harbor: any }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('harbor_management_update');

  const {
    data: accessInfo,
    isLoading,
    isError,
    error,
  } = useGetHarborAccessInformationQuery(harbor?.id, { skip: !harbor?.id });

  const [updateAccessInformation, { isLoading: isSaving }] =
    useUpdateHarborAccessInformationMutation();
  const [translateAccessInformation, { isLoading: isTranslatingPreview }] =
    useTranslateHarborAccessInformationMutation();

  const [accessCode, setAccessCode] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState('en');
  const [usageDescription, setUsageDescription] = useState('');
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

  useEffect(() => {
    if (!accessInfo) return;
    setAccessCode(accessInfo.access_code ?? '');
    setSourceLanguage(accessInfo.access_code_language ?? 'en');
    const nextTranslations =
      accessInfo.access_code_usage_description_translations ?? {};
    setTranslations(nextTranslations);
    setUsageDescription(
      nextTranslations[accessInfo.access_code_language ?? 'en'] ?? ''
    );
  }, [accessInfo]);

  useEffect(() => {
    if (!isError || !error) return;
    const apiErr = error as { data?: { message?: string } };
    enqueueSnackbar(
      apiErr?.data?.message ??
        intl.formatMessage({ id: 'HARBOR.ACCESS_INFO.LOAD_FAILED' }),
      { variant: 'error' }
    );
  }, [isError, error, enqueueSnackbar, intl]);

  const hasUnsavedCode = accessCode.trim().length > 0;

  const handleOpenPreview = useCallback(async () => {
    if (!usageDescription.trim()) return;

    // If translations already exist, open preview directly without calling the translate API
    if (Object.keys(translations).length > 0) {
      setIsPreviewModalOpen(true);
      return;
    }

    try {
      const result = await translateAccessInformation({
        access_code_usage_description: usageDescription,
        source_language: sourceLanguage,
      }).unwrap();
      if (result.warning_message) {
        enqueueSnackbar(result.warning_message, { variant: 'warning' });
      }
      if (result.translations) {
        setTranslations(result.translations);
      }
      setIsPreviewModalOpen(true);
    } catch (err) {
      const apiErr = err as { data?: { message?: string } };
      enqueueSnackbar(
        apiErr?.data?.message ??
          intl.formatMessage({ id: 'HARBOR.ACCESS_INFO.TRANSLATE_FAILED' }),
        { variant: 'error' }
      );
      // Still open preview modal so user can manually translate/view
      setIsPreviewModalOpen(true);
    }
  }, [
    usageDescription,
    sourceLanguage,
    translations,
    translateAccessInformation,
    enqueueSnackbar,
    intl,
  ]);

  const handleTranslationsUpdate = useCallback(
    (updated: Record<string, string>) => {
      setTranslations(updated);
    },
    []
  );

  const handleSourceDescriptionChange = useCallback((value: string) => {
    setUsageDescription(value);
  }, []);

  const handleSave = async () => {
    setFormError(null);
    if (hasUnsavedCode && !usageDescription.trim()) {
      setFormError(
        intl.formatMessage({
          id: 'HARBOR.ACCESS_INFO.USAGE_DESCRIPTION_REQUIRED',
        })
      );
      return;
    }
    try {
      const result = await updateAccessInformation({
        harbor_id: harbor.id,
        data: hasUnsavedCode
          ? {
              access_code: accessCode.trim(),
              access_code_usage_description: usageDescription.trim(),
              access_code_language: sourceLanguage,
              translations,
            }
          : { access_code: null, access_code_usage_description: null },
      }).unwrap();
      setAccessCode(result.access_code ?? '');
      setSourceLanguage(result.access_code_language ?? sourceLanguage);
      setTranslations(result.access_code_usage_description_translations ?? {});
      enqueueSnackbar(intl.formatMessage({ id: 'HARBOR.ACCESS_INFO.SAVED' }), {
        variant: 'success',
      });
    } catch (err) {
      const apiErr = err as { data?: { message?: string } };
      enqueueSnackbar(
        apiErr?.data?.message ??
          intl.formatMessage({ id: 'HARBOR.ACCESS_INFO.SAVE_FAILED' }),
        { variant: 'error' }
      );
    }
  };

  const handleClear = async () => {
    try {
      await updateAccessInformation({
        harbor_id: harbor.id,
        data: { access_code: null, access_code_usage_description: null },
      }).unwrap();
      setAccessCode('');
      setUsageDescription('');
      setTranslations({});
      enqueueSnackbar(
        intl.formatMessage({ id: 'HARBOR.ACCESS_INFO.CLEARED' }),
        {
          variant: 'success',
        }
      );
    } catch (err) {
      const apiErr = err as { data?: { message?: string } };
      enqueueSnackbar(
        apiErr?.data?.message ??
          intl.formatMessage({ id: 'HARBOR.ACCESS_INFO.SAVE_FAILED' }),
        { variant: 'error' }
      );
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center text-gray-500">
        {intl.formatMessage({ id: 'COMMON.LOADING' })}
      </div>
    );
  }

  const showPreviewButton = hasUnsavedCode && !!usageDescription.trim();

  return (
    <div>
      <div className="card card-grid">
        <CardHeader>
          <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-900">
            {intl.formatMessage({ id: 'HARBOR.ACCESS_INFO.TITLE' })}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-600 mt-1">
            {intl.formatMessage({ id: 'HARBOR.ACCESS_INFO.SUBTITLE' })}
          </p>
        </CardHeader>
        <CardContent className="px-6 pb-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
            <div className="col-span-1">
              <label htmlFor="harbor-access-code" className={labelClassName}>
                {intl.formatMessage({ id: 'HARBOR.ACCESS_INFO.ACCESS_CODE' })}
              </label>
              <input
                id="harbor-access-code"
                type="text"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                disabled={!canUpdate || isSaving}
                className={clsx(inputClassName, 'border-gray-300')}
                placeholder={intl.formatMessage({
                  id: 'HARBOR.ACCESS_INFO.ACCESS_CODE_PLACEHOLDER',
                })}
              />
            </div>

            <div className="col-span-1">
              <label
                htmlFor="harbor-access-source-language"
                className={labelClassName}
              >
                {intl.formatMessage({
                  id: 'HARBOR.ACCESS_INFO.SOURCE_LANGUAGE',
                })}
              </label>
              <select
                id="harbor-access-source-language"
                value={sourceLanguage}
                onChange={(e) => setSourceLanguage(e.target.value)}
                disabled={!canUpdate || isSaving}
                className={clsx(selectClassName, 'border-gray-300')}
              >
                <option value="en">English</option>
                <option value="fi">Finnish</option>
                <option value="sv">Swedish</option>
                <option value="de">German</option>
              </select>
            </div>

            <div className="col-span-1 md:col-span-2">
              <label
                htmlFor="harbor-access-usage-description"
                className={labelClassName}
              >
                {intl.formatMessage({
                  id: 'HARBOR.ACCESS_INFO.USAGE_DESCRIPTION',
                })}
              </label>
              <textarea
                id="harbor-access-usage-description"
                rows={4}
                value={usageDescription}
                onChange={(e) => setUsageDescription(e.target.value)}
                disabled={!canUpdate || isSaving}
                className={clsx(
                  textareaClassName,
                  formError ? 'border-red-500' : 'border-gray-300'
                )}
                placeholder={intl.formatMessage({
                  id: 'HARBOR.ACCESS_INFO.USAGE_DESCRIPTION_PLACEHOLDER',
                })}
              />
              {formError && (
                <p className="text-red-500 text-xs mt-1">{formError}</p>
              )}
            </div>
          </div>

          {/* Preview Message */}
          {canUpdate && showPreviewButton && (
            <div className="mt-6 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 border border-blue-200 dark:border-blue-800 contrast:border-blue-800 rounded-lg p-4 flex items-center justify-between gap-2">
              <div className="flex items-center">
                <KeenIcon
                  type="outline"
                  icon="paper-clip"
                  className="w-5 h-5 text-blue-600 dark:text-blue-100 contrast:text-blue-100 mr-2"
                />
                <p className="text-blue-700 dark:text-blue-100 contrast:text-blue-100 text-sm">
                  {intl.formatMessage({
                    id: 'HARBOR.ACCESS_INFO.PREVIEW_BANNER',
                  })}
                </p>
              </div>
              <button
                type="button"
                onClick={handleOpenPreview}
                disabled={isTranslatingPreview}
                className="btn btn-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 flex items-center gap-1.5"
              >
                {isTranslatingPreview && (
                  <svg
                    className="animate-spin h-3.5 w-3.5 text-white"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {isTranslatingPreview
                  ? intl.formatMessage({
                      id: 'HARBOR.ACCESS_INFO.PREPARING_PREVIEW',
                    })
                  : intl.formatMessage({
                      id: 'HARBOR.ACCESS_INFO.SHOW_PREVIEW',
                    })}
              </button>
            </div>
          )}

          {/* Action Buttons */}
          {canUpdate && (
            <div className="mt-8 flex justify-end gap-3">
              {accessInfo?.access_code && (
                <button
                  type="button"
                  onClick={() => setIsClearConfirmOpen(true)}
                  disabled={isSaving}
                  className="btn btn-sm btn-ghost text-red-600 bg-red-50 hover:bg-red-100 dark:bg-transparent dark:hover:bg-white/10 contrast:bg-transparent contrast:hover:bg-white/10 rounded-lg px-4 py-2"
                >
                  {intl.formatMessage({ id: 'HARBOR.ACCESS_INFO.CLEAR' })}
                </button>
              )}
              <button
                type="button"
                onClick={handleSave}
                className="btn btn-sm btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2"
                disabled={isSaving}
              >
                {isSaving
                  ? `${intl.formatMessage({ id: 'COMMON.SAVE' })}...`
                  : intl.formatMessage({ id: 'COMMON.SAVE' })}
              </button>
            </div>
          )}
        </CardContent>
      </div>

      {/* Preview Modal */}
      <HarborAccessInfoPreviewModal
        open={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        sourceLanguage={sourceLanguage}
        sourceUsageDescription={usageDescription}
        initialTranslations={translations}
        onTranslationsUpdate={handleTranslationsUpdate}
        onSourceDescriptionChange={handleSourceDescriptionChange}
      />

      {/* Clear Access Code Confirmation Dialog */}
      <Modal
        open={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        className="!flex items-center justify-center"
        aria-labelledby="clear-access-code-dialog-title"
        aria-describedby="clear-access-code-dialog-description"
        role="dialog"
        aria-modal="true"
      >
        <div className="bg-white dark:bg-coal-300 w-full max-w-md mx-4 rounded-xl shadow-xl">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-coal-200">
            <h2
              id="clear-access-code-dialog-title"
              className="text-lg font-semibold text-gray-800 dark:text-white"
            >
              Clear access code?
            </h2>
          </div>
          <div className="px-6 py-5">
            <p
              id="clear-access-code-dialog-description"
              className="text-sm text-gray-600 dark:text-gray-600"
            >
              This will permanently remove the access code and usage description from this harbor. This action cannot be undone.
            </p>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 dark:border-coal-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsClearConfirmOpen(false)}
              disabled={isSaving}
              className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
            >
              {intl.formatMessage({ id: 'COMMON.CANCEL' })}
            </button>
            <button
              type="button"
              onClick={async () => {
                setIsClearConfirmOpen(false);
                await handleClear();
              }}
              disabled={isSaving}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm transition-colors duration-200 disabled:opacity-50"
            >
              {intl.formatMessage({ id: 'HARBOR.ACCESS_INFO.CLEAR' })}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default HarborAccessInformation;
